import { FormattedTextString } from "client/Misc/commonTypes";
import { resolveFormattedTextString } from "client/Misc/utils";
import {
	ReactElement,
	RefObject,
	useContext,
	useMemo,
	useRef
} from "react";
import { ActionListItem } from "../ActionList/ActionListItem";
import { ButtonKind } from "../Button/Button";
import { ItemResponsiveBehavior } from "../CollapsibleList/CollapsibleList";
import { ToolbarContext, ToolbarPriority } from "./Toolbar";
import { ToolbarItem } from "./ToolbarItem";
import { FontIconName, SvgIconValue } from "../Icon/Icon";
import { useCallbackWithoutArguments } from "client/Hooks";
import { CopyButton } from "../CopyButton/CopyButton";
import { useCopyToClipboard } from "client/Hooks/useCopyToClipboard";

export type ToolbarCopyButtonProps = {
	value: string;
	kind: ButtonKind;
	text: string | FormattedTextString;
	icon?: FontIconName | SvgIconValue;
	onClick?(): void;
	trackingId?: string;
	responsiveBehavior?: ItemResponsiveBehavior;
	isDisabled?: boolean;
};

export const ToolbarCopyButton = (props: ToolbarCopyButtonProps): ReactElement => {
	const {
		value,
		kind,
		text,
		icon,
		responsiveBehavior,
		trackingId,
		isDisabled,
		onClick
	} = props;

	const priority = useMemo(
		(): number => {
			if (responsiveBehavior === "hide") {
				return ToolbarPriority.LowPriorityItem;
			}
			if (kind === "primary") {
				return ToolbarPriority.PrimaryButton;
			}

			return ToolbarPriority.Default;
		},
		[responsiveBehavior, kind]
	);

	const clickHandler: (() => void) | undefined = useCallbackWithoutArguments(onClick);
	const { buttonSize } = useContext(ToolbarContext);

	return (
		<ToolbarItem
			priority={priority}
			responsiveBehavior={responsiveBehavior}
			isDisabled={isDisabled}
			renderNormalMode={(
				ref,
				onKeyDown,
				tabIndex
			) => {
				return (
					<CopyButton
						ref={ref}
						onKeyDown={onKeyDown}
						tabIndex={tabIndex}
						kind={kind}
						text={text}
						icon={icon}
						value={value}
						onClick={clickHandler}
						trackingId={trackingId}
						isDisabled={isDisabled}
						size={buttonSize}
					/>
				);
			}}
			renderCollapsedMode={() => {
				return (
					<CollapsedMode
						text={resolveFormattedTextString(text)}
						icon={icon}
						value={value}
						isDisabled={isDisabled}
						onClick={onClick}
						trackingId={trackingId}
					/>
				);
			}}
		/>
	);
};
ToolbarCopyButton.displayName = "Toolbar.CopyButton";

export type CollapsedModeProps = {
	text: string;
	icon: FontIconName | SvgIconValue | undefined;
	onClick?(): void;
	isDisabled: boolean | undefined;
	value: string;
	trackingId: string | undefined;
};

export const CollapsedMode = (props: CollapsedModeProps): ReactElement => {
	const {
		text,
		icon,
		onClick,
		isDisabled,
		value,
		trackingId
	} = props;

	const ref: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);
	useCopyToClipboard(ref, value);

	return (
		<ActionListItem
			text={text}
			icon={icon}
			onClick={onClick}
			isDisabled={isDisabled}
			ref={ref}
			trackingId={trackingId}
		/>
	);
};
CollapsedMode.displayName = "CollapsedMode";